import { Component, OnInit } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { ChargingStationDto } from '@dto/chargingStationDto';
import { PricingDto } from '@dto/pricingDto';
import { TransactionDto } from '@dto/transactionDto';
import { ChargingStationsService } from '@services/entity/charging-stations.service';
import { PricingsService } from '@services/entity/pricings.service';
import { TransactionsService } from '@services/entity/transactions.service';
import { ToastService } from '@services/toast/toast.service';
import { ChargingStationRemoteTransactionRequest } from '@dto/chargingStationRemoteTransactionRequest';
import { latLng, marker, tileLayer, Map, icon, Layer, divIcon } from 'leaflet';
import { MapService } from '../../../services/map/map.service';
import { InvoiceDto } from '@dto/invoiceDto';
import { PaymentsService } from '@services/payments/payments.service';
import { plainToClass } from 'class-transformer';
import Swal from 'sweetalert2';
import { PublicTransactionSessionDto } from '@dto/publicTransactionSessionDto';
import { PublicRemoteStartRequestDto } from '@dto/publicRemoteStartRequestDto';

@Component({
  selector: 'app-public-charge',
  templateUrl: './public-charge.component.html',
  styleUrls: ['./public-charge.component.scss']
})
export class PublicChargeComponent implements OnInit {

  public chargingStation?: ChargingStationDto
  public pricing?: PricingDto
  public transaction?: TransactionDto
  public publicTransactionSession: PublicTransactionSessionDto;
  
  public email: string
  public chargingStationId: string
  // public chargingPointConnectorId: number = 1
  public paymentIntentId: string
  public storedPaymentIntentId: string
  public storedChargingStationId: string
  public startTime: string
  public price: number
  public duration: string = "--:--"
  public durationInterval: any
  public statusInterval: any
  public kwhCharged: number = 0
  public isFinishing: boolean = false
  public isFinished: boolean = false

  // User scanned QR-Code first, EV and EVSE still await connection
  public isEvConnected: boolean = false
  
  // Awaiting connection timed out, restart required
  public isNeedingRestart: boolean = false

  public branding: string
  private map: Map;
  public mapOptions = {
    zoom: 18,
    boxZoom: false,
    zoomControl: false,
    doubleClickZoom: false,
    dragging: false,
    layers: [
      tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        minZoom: 18,
        maxZoom: 18,
        attribution: '© OpenStreetMap',
      })
    ],
  }
  public markers: Layer[] = []

  constructor(
    private chargingStationsService: ChargingStationsService,
    private transactionsService: TransactionsService,
    private pricingsService: PricingsService,
    private paymentsService: PaymentsService,
    private decimalPipe: DecimalPipe,
    private route: ActivatedRoute,
    public toastService: ToastService,
    private router: Router,
    private mapService: MapService,
  ) { }

  ngOnInit(): void {
    this.validatePaymentIntent()
  }

  async validatePaymentIntent() {
    // Grab the session ID
    this.paymentIntentId = this.route.snapshot.queryParams['payment-intent'];
    this.chargingStationId = this.route.snapshot.queryParams['charging-station'];

    // If there is no session ID, go back to public charge plans page
    if (!this.paymentIntentId) {
      this.goBack()
    }

    // Check if we have a session information in local storage
    this.storedPaymentIntentId = localStorage.getItem('publicTransactionPaymentIntentId')
    this.storedChargingStationId = localStorage.getItem('publicTransactionChargingStationId')

    // Save the subscription ID in case of page reload
    if (!this.storedPaymentIntentId || this.storedPaymentIntentId != this.paymentIntentId) {
      localStorage.setItem('publicTransactionPaymentIntentId', this.paymentIntentId)
      localStorage.setItem('publicTransactionChargingStationId', this.chargingStationId)
    }

    if (this.chargingStationId != null) {
      await this.getChargingStation()
    }

    // Now get the subscription data
    this.paymentsService.validatePublicTransaction(this.paymentIntentId).subscribe(publicTransactionSession => {
      this.publicTransactionSession = publicTransactionSession;

      if (this.publicTransactionSession.stopTimestamp != null) {
        this.stopPublicTransactionSession()
        return
      }
      
      // If charging station is charging
      if (this.publicTransactionSession.chargingStationStatusId == 3) {
        // Clear and restart duration update (timer)
        clearInterval(this.durationInterval)
        this.durationInterval = window.setInterval(() => this.updateDuration(), 1000)
        
        // Clear and restart charging status update (meter)
        clearInterval(this.statusInterval)
        this.statusInterval = window.setInterval(() => this.updateStatus(), 10000)

      } else {
        // Continuously check the charging station status, if not yet charging
        clearInterval(this.statusInterval)
        this.statusInterval = window.setInterval(() => { this.updateStatus() }, 10000)
      }

      // Start the transaction now
      if (this.publicTransactionSession.chargingStationStatusId == 2 || this.publicTransactionSession.chargingStationStatusId == 6) {
        this.startTransaction()
      }
    }, error => {
      // this.router.navigate([`public-charge-plans?cs=${this.chargingStationId}`], { relativeTo: this.route })
      window.location.href = `${window.location.origin}/public-charge-plans?cs=${this.chargingStationId}`
      console.error(error)
    });
  }

  clearSession() {
    localStorage.removeItem('publicTransactionPaymentIntentId')
    localStorage.removeItem('publicTransactionChargingStationId')
  }

  async getChargingStation() {
    this.chargingStationsService.getChargingStationPublicDetails(this.chargingStationId).subscribe(chargingStation => {
      this.chargingStation = chargingStation
      
      
      for (const chargingPoint of this.chargingStation.chargingPoints) {
        if (chargingPoint.statusId == 6) {
          this.isEvConnected = true
          continue
        }
      }

      if (this.chargingStation != null) {
        this.getPricing()
      }
    }, error => {
      console.error(error)
    })
  }

  getPricing() {
    // Get the pricing for our charging station
    this.pricingsService.getPublicPricingOfCustomer(this.chargingStation.customerId).subscribe(pricing => {
      this.pricing = pricing
    }, error => {})
  }

  updateStatus() {
    // Check the charging station status
    this.paymentsService.getPublicTransactionSession(this.publicTransactionSession.id).subscribe(publicTransaction => {
      this.publicTransactionSession = publicTransaction
      
      // Calculate current kWh charged
      const current = this.publicTransactionSession.currentMeterValue
      const start = this.publicTransactionSession.startMeterValue
      if (current && start) this.kwhCharged = (current - start) / 1000

      // Calculate current price with given kWh charged
      if (this.pricing.price) {
        this.price = this.kwhCharged * this.pricing.price
      }
      
      // If EV and EVSE await connection for more than ~30 seconds,
      // then EVSE jumps back to Ready state,
      // thereby needs restart when connection is finally established
      if (this.publicTransactionSession.chargingStationStatusId == 2) {
        this.isEvConnected = false
        this.isNeedingRestart = true
      }
      
      // If EVSE again has Preparing state,
      // then we assume the EV and EVSE connection is finally established
      // and we can restart the transaction
      if (this.publicTransactionSession.chargingStationStatusId == 6 && this.isNeedingRestart) {
        this.isEvConnected = true
        this.isNeedingRestart = true
        // NOTE: Insecure, instead demand user interaction
        // this.startTransaction()
      }

      // Eventually show that the transaction has concluded
      if (this.publicTransactionSession.stopTimestamp != null) {
        clearInterval(this.statusInterval)
        this.stopPublicTransactionSession()
      }
    })
  }

  startTransaction() {
    const remoteStartRequest: ChargingStationRemoteTransactionRequest = { start: true }
    const publicRemoteStartRequest: PublicRemoteStartRequestDto = {
      url: `${window.location.origin}/public-charge?charging-station=${this.chargingStationId}&payment-intent=${this.paymentIntentId}`,
      email: localStorage.getItem('email'),
      request: remoteStartRequest
    }
    // Start the transaction of our charging station by handing over the subscription ID
    this.chargingStationsService.remoteStartPublicTransaction(
      this.chargingStationId,
      this.paymentIntentId,
      publicRemoteStartRequest
    ).subscribe(transaction => {
      
      // If the transaction start request was accepted
      if (transaction.status == 'Accepted') {
        this.isNeedingRestart = false

        // Clear and restart duration update (timer)
        clearInterval(this.durationInterval)
        this.durationInterval = window.setInterval(() => this.updateDuration(), 1000)
        
        // Clear and restart charging status update (meter)
        clearInterval(this.statusInterval)
        this.statusInterval = window.setInterval(() => this.updateStatus(), 5000)
      
      // If the transaction start request was denied
      } else if (transaction.status == 'Denied') {
        // then todo: throw user friendly error
      }
    }, error => {
      switch (error) {
        case "PaymentIntent is already captured":
          this.isFinished = true; break;
        
        default:
          const href = `${window.location.origin}/public-charge-plans?cs=${this.chargingStationId}`
          window.location.href = href
      }
    });
  }

  updateDuration() {
    if (!this.publicTransactionSession.startTimestamp) {
      this.duration = '00:00'
      return
    }

    // Calculate the time passed while charging
    // and at some point keep in mind the actual duration of our transaction
    var start = new Date(this.publicTransactionSession.startTimestamp).getTime()
    var end = Date.now()

    if (this.publicTransactionSession.stopTimestamp != null) {
      start = new Date(this.publicTransactionSession.startTimestamp.toString()).getTime() - 7200000
      if (this.publicTransactionSession.stopTimestamp != null) {
        end = new Date(this.publicTransactionSession.stopTimestamp.toString()).getTime() - 7200000
      }
    }

    const t = (end - start) / 1000 // seconds passed

    const hours = Math.floor(t / 60 / 60) || 0
    const minutes = Math.floor(t / 60 % 60) || 0
    const seconds = Math.floor(t % 60) || 0
    
    const s = seconds > 9 ? seconds : '0' + seconds
    const m = minutes > 9 ? minutes : '0' + minutes
    const h = hours
    
    this.duration = `${h > 0 ? h + ':' : ''}${m}:${s}`
  }

  stopCharging() {
    Swal.fire({
      title: 'Sind Sie sicher?',
      text: 'Ein beendeter Ladevorgang kann nicht wieder gestartet werden.',
      icon: 'warning',
      confirmButtonText: 'Ladevorgang beenden',
      confirmButtonColor: 'crimson',
      showCancelButton: true,
      cancelButtonText: 'Abbrechen',
      cancelButtonColor: ''
    }).then((result) => {
      if (result.isConfirmed) {
        this.stopTransaction()
      }
    })
  }

  stopTransaction() {
    this.isFinishing = true

    // Stop the transaction
    var remoteStopRequest: ChargingStationRemoteTransactionRequest = { start: false }
    this.chargingStationsService.remoteStopPublicTransaction(
      this.chargingStation.id,
      this.paymentIntentId,
      remoteStopRequest
    ).subscribe(transaction => {
      // If the stop transaction request was accepted
      if (transaction.status == 'Accepted') {
        // and wrap up the session
        this.stopPublicTransactionSession()
      }
    }, error => {});
  }
  
  stopPublicTransactionSession() {
    this.clearSession()

    this.isFinished = true

    // Calculate current kWh charged
    if (this.publicTransactionSession.stopMeterValue) {
      this.kwhCharged = (this.publicTransactionSession.stopMeterValue - this.publicTransactionSession.startMeterValue) / 1000
    }

    if (this.pricing.price != null) {
      const price = this.kwhCharged * this.pricing.price
      this.price = +(Math.round(Number(price + "e+2")) + "e-2")
      // this.price = Math.round(this.kwhCharged * this.pricing.price * 100) / 100
    }

    this.updateDuration()
  }

  updateMap() {
    if (!this.map || !this.chargingStation) return

    const lat = this.chargingStation.lat ?? null
    const lon = this.chargingStation.lon ?? null

    if (!lat || !lon) return

    this.map.setView(latLng(parseFloat(lat), parseFloat(lon)))
    this.map.attributionControl.setPosition('topright')
    const newMarker = marker(
      [parseFloat(lat), parseFloat(lon)],
      {
        icon: divIcon({
          html: `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-map-pin" id="map-marker">
              <path fill="#EF7C00" stroke="#EF7C00" d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle fill="#FFCB00" stroke="#FFCB00" cx="12" cy="10" r="3"></circle>
            </svg>
          `,
          className: '',
          iconSize: [48, 48],
          iconAnchor: [12, 24],
        })
      }
    )
    this.map.addLayer(newMarker)
    this.map.invalidateSize()
    window.dispatchEvent(new Event('resize'))
  }

  onMapReady(map: Map) {
    this.map = map
    this.map.invalidateSize()
    window.dispatchEvent(new Event('resize'))
    this.updateMap()
  }

  goBack() {
    if (this.chargingStationId) {
      const href = `${window.location.origin}/public-charge-plans?cs=${this.chargingStationId}`
      window.location.href = href
    } else {
      window.history.back()
    }
  }

  getFullYear() {
    return new Date().getFullYear()
  }
}
